import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Checkbox, Flex, Icon, Input, Text, Tooltip, useElement } from '@pega/cosmos-react-core';
import { DragDropManager, DragDropList } from '@pega/cosmos-react-dnd';
import type { DraggableItem, ItemRendererProps } from '@pega/cosmos-react-dnd';
import styled, { css } from 'styled-components';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';

const MIN_PHRASES = 1;
const MAX_PHRASES = 3;
const MAX_CHARACTERS = 30;

const DEFAULT_PHRASE_KEYS = ['agentic_phase_thinking', 'agentic_phase_analyzing', 'agentic_phase_generating'];

const normalizeMessages = (messages: string[]) => messages.map((message) => message.trim());

export interface PhraseData {
  label: string;
  statusMessage: string;
}

interface PhraseRendererData extends PhraseData {
  index: number;
  phraseCount: number;
  onMessageChange: (id: string, value: string) => void;
  onRemove: (id: string) => void;
  t: (key: string) => string;
}

interface AgenticLoaderConfigProps {
  initialEnabled?: boolean;
  initialPhrases?: string[];
  onSave?: (enabled: boolean, phrases: string[]) => Promise<boolean>;
  onToggle?: (enabled: boolean, phrases: string[]) => Promise<boolean>;
  onChange?: (enabled: boolean, phrases: string[]) => void;
}

const StyledContainer = styled(Flex)(
  ({ theme }) => css`
    margin-block-start: calc(4 * ${theme.base.spacing});
  `,
);

const StyledDragItem = styled(Flex)<{ $isDragging?: boolean }>(
  ({ theme, $isDragging }) => css`
    padding: ${theme.base.spacing};
    margin: calc(0.5 * ${theme.base.spacing});
    border: 0.0625rem solid ${theme.base.colors.gray.light};
    border-radius: ${theme.base['border-radius']};
    background: ${theme.base.colors.gray['extra-light']};
    opacity: ${$isDragging ? 0.4 : 1};

    @media (max-width: 576px) {
      flex-wrap: wrap;
      row-gap: calc(0.5 * ${theme.base.spacing});
    }
  `,
);

const StyledPhraseContent = styled(Flex)(
  () => css`
    flex: 1;
    min-width: 0;
  `,
);

const StyledPhraseLabel = styled(Text)(
  ({ theme }) => css`
    white-space: nowrap;
    flex: 0 0 auto;
    padding: calc(0.25 * ${theme.base.spacing});
    align-self: center;
  `,
);

const StyledInputContainer = styled(Flex)(
  () => css`
    flex: 1;
    min-width: 0;

    & > * {
      width: 100%;
    }
  `,
);

const StyledAddButton = styled(Button)(
  ({ theme }) => css`
    gap: calc(0.5 * ${theme.base.spacing});
  `,
);

const StyledDragHandle = styled(Flex)(
  () => css`
    cursor: grab;
    padding-top: 0.25rem;
    align-self: center;
  `,
);

const generateId = () =>
  `phrase-${
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  }`;

interface PhraseInputProps {
  phraseId: string;
  value: string;
  placeholder: string;
  ariaLabel: string;
  onChange: (id: string, value: string) => void;
}

const PhraseInput: React.FC<PhraseInputProps> = React.memo(({ phraseId, value, placeholder, ariaLabel, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  const skipNextSync = useRef(false);

  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
    } else {
      setLocalValue(value);
    }
  }, [value]);

  return (
    <Input
      aria-label={ariaLabel}
      value={localValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        skipNextSync.current = true;
        setLocalValue(e.target.value);
        onChange(phraseId, e.target.value);
      }}
      placeholder={placeholder}
      maxLength={MAX_CHARACTERS}
    />
  );
});

const PhraseItemRenderer: React.FC<ItemRendererProps<PhraseRendererData>> = ({
  data,
  id,
  dragRef,
  previewRef,
  isDragging,
}) => {
  if (!data) return null;
  const { label, statusMessage, index, phraseCount, onMessageChange, onRemove, t } = data;

  const placeholderKey = DEFAULT_PHRASE_KEYS[index] || DEFAULT_PHRASE_KEYS[0];
  const placeholderText = t(placeholderKey);

  return (
    <StyledDragItem
      ref={previewRef}
      $isDragging={isDragging}
      container={{ direction: 'row', alignItems: 'start', itemGap: 1 }}
    >
      <StyledDragHandle
        ref={dragRef}
        container={{ alignItems: 'center' }}
        aria-label={`${t('drag_reorder')} ${label}`}
        aria-roledescription="sortable"
        tabIndex={0}
      >
        <Icon name="drag" aria-hidden="true" />
      </StyledDragHandle>

      <StyledPhraseContent container={{ direction: 'row', alignItems: 'start', itemGap: 0.5 }} item={{ grow: 1 }}>
        <StyledPhraseLabel variant="h4">{label}</StyledPhraseLabel>
        <StyledInputContainer>
          <PhraseInput
            phraseId={id}
            value={statusMessage}
            ariaLabel={`${t('status_message')} ${label}`}
            placeholder={`${t('phase_placeholder_prefix')} ${placeholderText}`}
            onChange={onMessageChange}
          />
        </StyledInputContainer>
      </StyledPhraseContent>

      {phraseCount > MIN_PHRASES && (
        <Button variant="simple" icon onClick={() => onRemove(id)} aria-label={`${t('remove_phase')} ${label}`}>
          <Icon name="trash" aria-hidden="true" />
        </Button>
      )}
    </StyledDragItem>
  );
};

const AgenticLoaderConfig: React.FC<AgenticLoaderConfigProps> = ({
  initialEnabled,
  initialPhrases,
  onSave,
  onToggle,
  onChange,
}) => {
  const translate = useLocalTranslation();

  const defaultPhrases = useMemo(() => DEFAULT_PHRASE_KEYS.map((key) => translate(key)), [translate]);

  const [isMultiPhaseLoaderEnabled, setIsMultiPhaseLoaderEnabled] = useState(initialEnabled ?? false);
  const [phrases, setPhrases] = useState<Array<{ id: string; statusMessage: string }>>(() => {
    const messages =
      initialPhrases && initialPhrases.length > 0 ? initialPhrases.slice(0, MAX_PHRASES) : defaultPhrases;
    return messages.map((msg) => ({
      id: generateId(),
      statusMessage: msg,
    }));
  });

  const [isDirty, setIsDirty] = useState(false);
  const savedPhrasesRef = useRef<string[]>(
    normalizeMessages(
      initialPhrases && initialPhrases.length > 0 ? initialPhrases.slice(0, MAX_PHRASES) : defaultPhrases,
    ),
  );
  const savedEnabledRef = useRef(initialEnabled ?? false);

  const hasChanges = useCallback((currentPhrases: Array<{ id: string; statusMessage: string }>) => {
    const currentMessages = normalizeMessages(currentPhrases.map((p) => p.statusMessage));
    const savedMessages = normalizeMessages(savedPhrasesRef.current);

    if (currentMessages.length !== savedMessages.length) return true;
    return currentMessages.some((msg, idx) => msg !== savedMessages[idx]);
  }, []);

  useEffect(() => {
    const newEnabled = initialEnabled ?? false;
    setIsMultiPhaseLoaderEnabled(newEnabled);
    savedEnabledRef.current = newEnabled;
  }, [initialEnabled]);

  useEffect(() => {
    if (initialPhrases && initialPhrases.length > 0) {
      const clampedPhrases = normalizeMessages(initialPhrases.slice(0, MAX_PHRASES));
      const newPhrases = clampedPhrases.map((msg) => ({
        id: generateId(),
        statusMessage: msg,
      }));
      setPhrases(newPhrases);
      savedPhrasesRef.current = clampedPhrases;
      setIsDirty(false);
    } else {
      const fallback = savedPhrasesRef.current.length > 0 ? savedPhrasesRef.current : defaultPhrases;
      const resetPhrases = fallback.map((msg) => ({
        id: generateId(),
        statusMessage: msg,
      }));
      setPhrases(resetPhrases);
      setIsDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPhrases]);

  const handleToggle = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      const prevEnabled = isMultiPhaseLoaderEnabled;
      const prevPhrases = phrases;
      const prevIsDirty = isDirty;

      setIsMultiPhaseLoaderEnabled(checked);

      // Use last saved phrases (do NOT persist unsaved local edits when toggling)
      const savedMessages =
        savedPhrasesRef.current && savedPhrasesRef.current.length > 0 ? savedPhrasesRef.current : defaultPhrases;
      const phrasesToSend = savedMessages.map((m) => m.trim());

      // Update local UI to reflect the last-saved values (discard unsaved edits)
      setPhrases(
        phrasesToSend.map((msg) => ({
          id: generateId(),
          statusMessage: msg,
        })),
      );
      setIsDirty(false);
      onChange?.(checked, phrasesToSend);

      if (onToggle) {
        const success = await onToggle(checked, phrasesToSend);
        if (!success) {
          // revert optimistic updates on failure
          setIsMultiPhaseLoaderEnabled(prevEnabled);
          setPhrases(prevPhrases);
          setIsDirty(prevIsDirty);
          onChange?.(
            prevEnabled,
            prevPhrases.map((p) => p.statusMessage),
          );
        } else {
          // success: record saved values
          savedEnabledRef.current = checked;
          savedPhrasesRef.current = phrasesToSend;
          setIsDirty(false);
        }
      } else {
        const phrasesChanged =
          phrasesToSend.length !== savedPhrasesRef.current.length ||
          phrasesToSend.some((msg, idx) => msg !== savedPhrasesRef.current[idx]);
        setIsDirty(checked !== savedEnabledRef.current || phrasesChanged);
      }
    },
    [onToggle, onChange, phrases, isMultiPhaseLoaderEnabled, isDirty, defaultPhrases],
  );

  const handlePhraseMessageChange = useCallback(
    (id: string, value: string) => {
      setPhrases((prev) => {
        const updated = prev.map((phrase) => (phrase.id === id ? { ...phrase, statusMessage: value } : phrase));
        onChange?.(
          isMultiPhaseLoaderEnabled,
          updated.map((p) => p.statusMessage),
        );
        setIsDirty(isMultiPhaseLoaderEnabled !== savedEnabledRef.current || hasChanges(updated));
        return updated;
      });
    },
    [onChange, isMultiPhaseLoaderEnabled, hasChanges],
  );

  const handleRemovePhrase = useCallback(
    (id: string) => {
      setPhrases((prev) => {
        if (prev.length <= MIN_PHRASES) return prev;
        const updated = prev.filter((phrase) => phrase.id !== id);
        onChange?.(
          isMultiPhaseLoaderEnabled,
          updated.map((p) => p.statusMessage),
        );
        setIsDirty(isMultiPhaseLoaderEnabled !== savedEnabledRef.current || hasChanges(updated));
        return updated;
      });
    },
    [onChange, isMultiPhaseLoaderEnabled, hasChanges],
  );

  const handleAddPhrase = useCallback(() => {
    setPhrases((prev) => {
      if (prev.length >= MAX_PHRASES) return prev;
      const updated = [
        ...prev,
        {
          id: generateId(),
          statusMessage: '',
        },
      ];
      onChange?.(
        isMultiPhaseLoaderEnabled,
        updated.map((p) => p.statusMessage),
      );
      setIsDirty(isMultiPhaseLoaderEnabled !== savedEnabledRef.current || hasChanges(updated));
      return updated;
    });
  }, [onChange, isMultiPhaseLoaderEnabled, hasChanges]);
  const handleDragDropChange = useCallback(
    (newItems: DraggableItem<PhraseRendererData>[]) => {
      setPhrases((prev) => {
        const idOrderMap = new Map(newItems.map((item, idx) => [item.id, idx]));
        const updated = [...prev].sort((a, b) => {
          const aIdx = idOrderMap.get(a.id) ?? 0;
          const bIdx = idOrderMap.get(b.id) ?? 0;
          return aIdx - bIdx;
        });
        onChange?.(
          isMultiPhaseLoaderEnabled,
          updated.map((p) => p.statusMessage),
        );
        setIsDirty(isMultiPhaseLoaderEnabled !== savedEnabledRef.current || hasChanges(updated));
        return updated;
      });
    },
    [onChange, isMultiPhaseLoaderEnabled, hasChanges],
  );

  const isDefault = useMemo(() => {
    if (phrases.length !== defaultPhrases.length) return false;
    return phrases.every((p, i) => p.statusMessage.trim() === defaultPhrases[i]);
  }, [phrases, defaultPhrases]);

  const handleResetToDefault = useCallback(() => {
    const resetPhrases = defaultPhrases.map((msg) => ({
      id: generateId(),
      statusMessage: msg,
    }));
    setPhrases(resetPhrases);
    setIsDirty(isMultiPhaseLoaderEnabled !== savedEnabledRef.current || hasChanges(resetPhrases));
    onChange?.(isMultiPhaseLoaderEnabled, defaultPhrases);
  }, [defaultPhrases, onChange, isMultiPhaseLoaderEnabled, hasChanges]);

  const hasEmptyFields = useMemo(() => {
    return phrases.some((p) => !p.statusMessage.trim());
  }, [phrases]);

  const handleSave = useCallback(async () => {
    if (onSave) {
      const phraseMessages = phrases.map((p) => p.statusMessage.trim());
      const success = await onSave(isMultiPhaseLoaderEnabled, phraseMessages);
      if (success) {
        setPhrases((prev) =>
          prev.map((phrase, index) => ({
            ...phrase,
            statusMessage: phraseMessages[index] ?? phrase.statusMessage,
          })),
        );
        savedPhrasesRef.current = phraseMessages;
        savedEnabledRef.current = isMultiPhaseLoaderEnabled;
        setIsDirty(false);
        onChange?.(isMultiPhaseLoaderEnabled, phraseMessages);
      }
    }
  }, [onSave, isMultiPhaseLoaderEnabled, phrases, onChange]);

  const [saveButtonEl, setSaveButtonEl] = useElement();
  const [addPhraseButtonEl, setAddPhraseButtonEl] = useElement();
  const [resetButtonEl, setResetButtonEl] = useElement();

  const isSaveDisabled = !isDirty || hasEmptyFields;
  const saveTooltipMessage = hasEmptyFields
    ? translate('save_disabled_empty_fields')
    : !isDirty
    ? translate('save_disabled_no_changes')
    : '';

  const isAddPhraseDisabled = phrases.length >= MAX_PHRASES;
  const addPhraseTooltipMessage = isAddPhraseDisabled ? translate('add_phrase_disabled_max_reached') : '';

  const isResetDisabled = isDefault;
  const resetTooltipMessage = isResetDisabled ? translate('reset_disabled_already_default') : '';

  const dragDropItems: DraggableItem<PhraseRendererData>[] = phrases.map((phrase, index) => ({
    id: phrase.id,
    type: 'phrase',
    data: {
      label: `${translate('phase_label')} ${index + 1}`,
      statusMessage: phrase.statusMessage,
      index,
      phraseCount: phrases.length,
      onMessageChange: handlePhraseMessageChange,
      onRemove: handleRemovePhrase,
      t: translate,
    },
  }));

  return (
    <StyledContainer container={{ direction: 'column', itemGap: 1.5 }}>
      <Text variant="h3" as="h3">
        {translate('agentic_configurations')}
      </Text>

      <Flex container={{ direction: 'column', itemGap: 0.5 }}>
        <Checkbox
          label={translate('enable_multi_phase_loader')}
          name="enableMultiPhaseLoader"
          checked={isMultiPhaseLoaderEnabled}
          onChange={handleToggle}
        />
        <Text variant="secondary">{translate('enable_multi_phase_loader_helper')}</Text>
      </Flex>

      {isMultiPhaseLoaderEnabled && (
        <Flex container={{ direction: 'column', itemGap: 1.5 }}>
          <DragDropManager>
            <DragDropList<PhraseRendererData>
              accept="phrase"
              items={dragDropItems}
              dragHandles
              itemRenderer={PhraseItemRenderer}
              onChange={handleDragDropChange}
            />
          </DragDropManager>

          <Flex
            container={{
              direction: 'row',
              justify: 'between',
              alignItems: 'center',
            }}
          >
            <Flex ref={setAddPhraseButtonEl}>
              <StyledAddButton variant="secondary" onClick={handleAddPhrase} disabled={isAddPhraseDisabled}>
                <Icon name="plus" aria-hidden="true" />
                {translate('add_phrase')}
              </StyledAddButton>
              {isAddPhraseDisabled && addPhraseButtonEl && (
                <Tooltip target={addPhraseButtonEl}>{addPhraseTooltipMessage}</Tooltip>
              )}
            </Flex>
            <Flex container={{ direction: 'row', itemGap: 1, alignItems: 'center' }}>
              <Flex ref={setResetButtonEl}>
                <Button variant="secondary" onClick={handleResetToDefault} disabled={isResetDisabled}>
                  {translate('reset_to_default')}
                </Button>
                {isResetDisabled && resetButtonEl && <Tooltip target={resetButtonEl}>{resetTooltipMessage}</Tooltip>}
              </Flex>
              <Flex ref={setSaveButtonEl}>
                <Button variant="primary" onClick={handleSave} disabled={isSaveDisabled}>
                  {translate('save')}
                </Button>
                {isSaveDisabled && saveButtonEl && <Tooltip target={saveButtonEl}>{saveTooltipMessage}</Tooltip>}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      )}
    </StyledContainer>
  );
};

export default AgenticLoaderConfig;
